
import { Component, Mixins, Prop, Vue, Watch } from 'vue-property-decorator';
import { IdState } from 'vue-virtual-scroller';
import { namespace } from 'vuex-class';

import BackendTrackingMixin from '@/mixins/BackendTracking.vue';
import ImpressionTrackingMixin from '@/mixins/ImpressionTrackingMixin.vue';
import TitleWatchlistMutationMixin from '@/mixins/title-quick-action-mutations/TitleWatchlistMutationMixin.vue';

const MarkAsSeenModalGraphql = () => import('@/components/mark-as-seen/MarkAsSeenModalContainerGraphql.vue');
const TitleScoreList = () => import('@/components/TitleScoreList.vue');

import WatchNowButton from '@/components/buttons/WatchNowButton.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import ImpressionPixel from '@/components/sponsored-recommendations/ImpressionPixel.vue';
import RecommendationAdPill from '@/components/sponsored-recommendations/RecommendationAdPill.vue';

import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowModuleContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers/snowplow-contexts';

import {
	ExternalTracker,
	ExternalTrackerType,
	MonetizationType,
	ObjectType,
	SraEventAction,
} from '@/@types/graphql-types';
import { ModalHelper } from '@/helpers/modal-helper';
import type { MarkAsSeenModalProps } from '../mark-as-seen/MarkAsSeen';
import { MarkAsSeenModalOptions } from '../mark-as-seen/MarkAsSeen';

import { ImpressionTrackingEvents } from '@/enums/events';

import { ClickoutUrl } from '@/helpers/clickout-helper';
import type { ListMutationTitleDetailParam } from '@/helpers/providers/title-actions-provider';
import type { SponsoredAdFragment } from '@/pages/graphql/fragments/SponsoredAd.fragment';
import type { SRClickTitleEvent, SingleTitleSR } from '@/components/sponsored-recommendations/types';
import { AssertByTypename } from '@/helpers/graphql-model-helper';
import { ReadyType } from '@/stores/ready.store';

const IdStateMixin = IdState({
	idProp: (vm: SponsoredRecommendationPoster) =>
		vm.campaignNode.id ? 'LargeNativeAdPoster' + vm.campaignNode.id : 'Poster-NOID',
});

const Language = namespace('language');
const TitleDetailsStore = namespace('titleDetails');
const User = namespace('user');

@Component<SponsoredRecommendationPoster>({
	name: 'SponsoredRecommendationPoster',
	components: {
		ImpressionPixel,
		ProviderIcon,
		RecommendationAdPill,
		TitleScoreList,
		WatchNowButton,
	},
	// virtual scroller persistent state
	idState() {
		return {
			imageLoaded: false,
		};
	},
})
export default class SponsoredRecommendationPoster extends Mixins(
	BackendTrackingMixin,
	ImpressionTrackingMixin,
	IdStateMixin,
	TitleWatchlistMutationMixin
) {
	impressionPixelsInternal: Array<string> = [];
	impressionPixelsExternal: Array<string> = [];

	@Prop({ default: () => [] }) declare additionalContexts: (SnowplowModuleContext | SnowplowContext)[];
	@Prop({ required: true }) declare sponsoredAd: SponsoredAdFragment;
	@Prop({ default: true }) lazy: boolean;

	@Language.Getter declare language: string;

	@TitleDetailsStore.Action setSponsoredRecommendation: (sponsoredAd: SponsoredAdFragment) => void;

	@User.Getter strictlyAllConsentsGranted: boolean;

	@Watch('isLoggedIn', { immediate: true })
	onIsLoggedInChange() {
		const { id, details } = this.loginStateTitleDetails;
		if (this.isLoggedIn()) {
			this.mixin_onIsLoggedInChange(
				id,
				details,
				this.isInWatchlist,
				ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS
			);
		}
	}

	/** IMPRESSION TRACKING */
	get observable() {
		return this.getObservableOptions(this.sponsoredAd?.campaign?.node.id);
	}
	/** IMPRESSION TRACKING */

	get loginStateTitleDetails() {
		return {
			id: this.campaignNode.id,
			details: {
				title: this.campaignNode.content.title,
				objectType: this.titleObjectType,
				objectId: this.titleObjectId,
			},
		};
	}

	get isInWatchlist() {
		return !!this.campaignNode?.watchlistEntryV2?.createdAt;
	}

	get promotionalImageUrl() {
		return this.sponsoredAd.campaign.promotionalImageUrl as string;
	}

	get posterKey() {
		return this.promotionalImageUrl || this.titleObjectId;
	}

	get campaignNode() {
		return this.sponsoredAd.campaign!.node as AssertByTypename<
			SponsoredAdFragment['campaign']['node'],
			'Movie' | 'Show'
		>;
	}

	get watchNowLabel() {
		return this.sponsoredAd.campaign.watchNowLabel;
	}

	get promotionalTitle() {
		return this.sponsoredAd.campaign?.promotionalTitle || this.campaignNode.content.title;
	}

	get promotionalText() {
		return this.sponsoredAd.campaign?.promotionalText || this.campaignNode.content.originalReleaseYear;
	}

	get impressionTagInternal() {
		return '';
	}

	get impressionTagExternal() {
		return '';
	}

	/**
	 * Experiment: MGM_REBRAND_BURST
	 * Overrides the campaign with the MGM Plus logo
	 */
	get hasMGMCampaign() {
		return this.sponsoredAd.campaign.name.includes('MGM_REBRAND_BURST');
	}

	get providerId() {
		return this.sponsoredAd.campaign.watchNowOffer.package.packageId;
	}

	get seenEpisodeCount() {
		return 'seenState' in this.campaignNode ? this.campaignNode.seenState?.seenEpisodeCount : null;
	}

	get watchNowOffer() {
		return this.sponsoredAd.campaign.watchNowOffer;
	}

	get titleContext() {
		return new SnowplowTitleContextGraphql(this.titleObjectId, this.titleObjectType);
	}

	get clickoutContext() {
		return SnowplowClickoutContextGraphql.fromProviderOffer(this.watchNowOffer);
	}

	get watchNowUctLink() {
		const clickoutUrl = new ClickoutUrl(this.watchNowOffer).set('uct_country', this.country).set('uct_tryout', 1);
		return clickoutUrl.toString();
	}

	get isCinemaSR() {
		return this.sponsoredAd.campaign?.watchNowOffer.monetizationType === MonetizationType.Cinema;
	}

	async mounted() {
		this.injectExternalImpressionHtml();
	}

	// needed by ImpressionTrackingMixin
	async impressionCallback() {
		this.$backendTracking.trackSraEvent(SraEventAction.Impression, this.sponsoredAd);

		// External tracking
		this.externalTrackingImpressionUrl();

		if (this.impressionTagInternal) this.addImpressionTrackingPixelInternal();
		if (this.impressionTagExternal) this.addImpressionTrackingPixelExternal();
	}

	async watchNowClicked() {
		this.$backendTracking.trackSraEvent(SraEventAction.WatchnowClicked, this.sponsoredAd);

		// External tracking
		this.externalTrackingClickoutUrl();
	}

	// External tracking
	get externalClickoutUrl(): ExternalTracker | undefined {
		return this.sponsoredAd.campaign.externalTrackers.find(
			(e: ExternalTracker) => e.type === ExternalTrackerType.ClickoutUrl
		);
	}

	private async externalTrackingClickoutUrl() {
		if (await this.hasStrictlyAllConsentsGranted()) {
			if (this.externalClickoutUrl) {
				const url = this.externalClickoutUrl.data;
				try {
					fetch(url, {
						mode: 'no-cors',
					});
				} catch (err: any) {}
			}
		}
	}

	get externalImpressionUrl(): ExternalTracker | undefined {
		return this.sponsoredAd.campaign.externalTrackers.find(
			(e: ExternalTracker) => e.type === ExternalTrackerType.ImpressionUrl
		);
	}

	get externalImpressionHtml(): ExternalTracker[] {
		return this.sponsoredAd.campaign.externalTrackers.filter(
			(e: ExternalTracker) => e.type === ExternalTrackerType.ImpressionHtml
		);
	}

	async toggleWatchlist(title: typeof this.campaignNode) {
		this.$backendTracking.trackSraEvent(SraEventAction.WatchlistClicked, this.sponsoredAd);

		const titleDetails: ListMutationTitleDetailParam = {
			title: title.content.title,
			objectType: title.objectType,
			objectId: title.objectId,
		};
		const trackingPayload = {
			source: 'sponsored-recommendations',
		};
		const updatedTitle = await this.mixin_setInWatchlist(
			title.id,
			this.isInWatchlist,
			titleDetails,
			trackingPayload
		);
		if (updatedTitle) {
			const isInWatchlist = !!updatedTitle.data?.setInWatchlistV2.title.watchlistEntryV2?.createdAt;
			if (
				isInWatchlist &&
				(title.objectType === ObjectType.Show || title.objectType === ObjectType.ShowSeason) &&
				title.content.isReleased
			) {
				ModalHelper.openModal(
					MarkAsSeenModalGraphql,
					{
						titleId: title.id,
						titleName: title.content.title,
						titleObjectId: title.objectId,
						titleObjectType: title.objectType,
						titleContentType: undefined,
						posterUrl: title.content.posterUrl,
						backdrops: title.content.backdrops,
						seenEpisodeCount: this.seenEpisodeCount,
					} as MarkAsSeenModalProps,
					MarkAsSeenModalOptions,
					'',
					false,
					this.additionalContexts
				);
			}
		}
	}

	onPosterClick() {
		this.setSponsoredRecommendation(this.sponsoredAd);

		this.$backendTracking.trackSraEvent(SraEventAction.Click, this.sponsoredAd);
		this.$backendTracking.trackSraEvent(SraEventAction.Clickout, this.sponsoredAd);

		// External tracking
		this.externalTrackingClickoutUrl();

		// pass down poster-clicked event as 'click'
		const clickTitleEvent: SRClickTitleEvent<SingleTitleSR> = { title: this.campaignNode, index: 0 };
		this.$emit('click', clickTitleEvent);

		window.open(this.watchNowUctLink, '_blank');
	}

	onMoreInfoClick() {
		this.setSponsoredRecommendation(this.sponsoredAd);

		this.$backendTracking.trackSraEvent(SraEventAction.MoreInfoClicked, this.sponsoredAd);

		// navigate to title detail page
		const clickTitleEvent: SRClickTitleEvent<SingleTitleSR> = { title: this.campaignNode, index: 0 };
		this.$emit('navigateTitle', clickTitleEvent);
	}

	private async hasStrictlyAllConsentsGranted() {
		await Vue.$jw.ready?.waitFor(ReadyType.CONSENT_PARTNERS_ANSWERED);
		return this.strictlyAllConsentsGranted;
	}

	private async externalTrackingImpressionUrl() {
		if (await this.hasStrictlyAllConsentsGranted()) {
			if (this.externalImpressionUrl) {
				const url = this.externalImpressionUrl.data;
				try {
					fetch(url, {
						mode: 'no-cors',
					});
				} catch (err: any) {}
			}
		}
	}

	private async injectExternalImpressionHtml() {
		if (this.externalImpressionHtml.length === 0) return;

		if (await this.hasStrictlyAllConsentsGranted()) {
			this.externalImpressionHtml.forEach(({ data }) => {
				if (data) {
					const fragment = document.createRange().createContextualFragment(data);
					document.head.appendChild(fragment);
				}
			});
		}
	}

	private addImpressionTrackingPixelInternal() {
		this.impressionPixelsInternal.push('_');
	}

	private async addImpressionTrackingPixelExternal() {
		if (await this.hasStrictlyAllConsentsGranted()) {
			this.impressionPixelsExternal.push('_');
		}
	}
}
